/**
 * @file BlendAllocationBar.js
 * @version 2.2.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-25
 * @modified 2026-09-17
 * @description The blend as one proportional line.
 *
 *              It began as a drag-to-allocate control: grab a segment, pull it,
 *              watch the others give way. The maths was sound and the gesture
 *              was wrong for the job — a blend is set to figures somebody has
 *              decided on, typed to two decimals, not nudged towards by eye.
 *              Dragging made a precise task feel approximate and put a slider
 *              between the user and a number they already knew.
 *
 *              What survives is the part that earned its place: the shape. One
 *              line, sections in the same order as the rows in the grid, each
 *              the colour of its row's left border, so a material can be found
 *              in the table by the colour it has on the line, and the balance
 *              of the blend read without reading any numbers at all.
 *
 *              Comparing shows two lines at equal weight: A, the blend being
 *              built, above B, the one it is held against. Neither is a ghost of
 *              the other — they are two blends being looked at side by side.
 */

import React from 'react';
import * as blendMath from '../utils/blendMath';
import { isIncluded } from '../utils/blendInclusion';
import './BlendAllocationBar.css';

/**
 * A stable colour per material, drawn from the brand.
 *
 * The first version hashed to any hue on the wheel, which put twelve unrelated
 * colours side by side; the second used one designed sweep, which read as a
 * chart borrowed from somewhere else. This is the house palette — the navy, the
 * blue and the gold off the letterhead — extended with greys spaced far enough
 * apart in lightness to stay separable when four land in a row.
 *
 * Ordered so consecutive entries contrast, and reds are absent on purpose: they
 * belong to the out-of-spec and over-100 states, and a material that hashed red
 * would read as a warning.
 */
const SEGMENT_COLOURS = [
  '#1e3a5f', // excel navy
  '#c9a961', // excel gold
  '#6b7378', // mid grey
  '#4f6475', // excel blue
  '#c3c8cb', // pale grey
  '#2f5d7c', // navy-blue
  '#8697a4', // excel light blue
  '#3f464c', // charcoal
  '#a8863f', // deep gold
  '#9aa1a6'  // light grey
];

/**
 * Hashed by key rather than assigned by position.
 *
 * Assigning by index would guarantee neighbours contrast, but it would also
 * re-colour the whole line every time a row was inserted — and the grid's row
 * borders have to agree with this, so a colour that moves is a colour that
 * lies.
 */
export function colourFor(key) {
  const s = String(key);
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return SEGMENT_COLOURS[hash % SEGMENT_COLOURS.length];
}

/**
 * One line. `rows` arrives already in grid order. `allocated` includes anything
 * too small to draw — otherwise a material at 0.01% would reappear as 0.01%
 * "unallocated".
 */
function Line({ rows, allocated, label }) {
  const gap = Math.round((100 - allocated) * 100) / 100;

  return (
    <div className="ba-line-row">
      {label && (
        <span className={`blend-ab-tag is-${label === 'A' ? 'a' : 'b'} ba-line-tag`}>
          {label}
        </span>
      )}
      <div className="ba-bar">
        {rows.map((row) => (
          <div
            key={row.key}
            className="ba-seg"
            style={{ width: `${row.value}%`, background: colourFor(row.key) }}
            title={`${row.name} — ${row.value.toFixed(2)}%`}
          />
        ))}
        {/* Already rounded to hundredths, so anything above zero shows as one. */}
        {gap > 0 && (
          <div className="ba-seg ba-gap" style={{ width: `${gap}%` }}
               title={`${gap.toFixed(2)}% unallocated`} />
        )}
      </div>
    </div>
  );
}

function BlendAllocationBar({
  materials = [],
  draft,
  // The order the rows appear in the grid. The line follows it, so a section
  // and its row are found by the same colour in the same sequence.
  order = null,
  // Comparing: the reference blend, drawn as a second line of equal weight
  // rather than as a ghost behind this one.
  compareValues = null
}) {
  const byKey = new Map(materials.map((m) => [m.key, m]));

  const rowsFrom = (values) => {
    const source = values || {};
    const keys = order && order.length
      ? order.filter((k) => source[k] !== undefined)
      : Object.keys(source);
    const all = keys.map((key) => ({
      key,
      value: blendMath.toNumber(source[key]) || 0,
      name: byKey.get(key)?.name || key
    }));
    return {
      // The same "in the blend" as the ingredient count and the printed sheet.
      rows: all.filter((r) => isIncluded(r.value)),
      allocated: all.reduce((s, r) => s + Math.max(0, r.value), 0)
    };
  };

  const live = rowsFrom(draft.values);
  const reference = compareValues ? rowsFrom(compareValues) : null;

  if (!live.rows.length && !reference?.rows.length) {
    return (
      <div className="blend-alloc-empty">
        Add a material and the blend appears here as a proportional line.
      </div>
    );
  }

  return (
    <div className="blend-alloc">
      {/* A is the blend being built and leads; B is the reference held beneath
          it. The same letters the grid columns and the summary use. Unlabelled
          when there is one line, because there is nothing to tell apart. */}
      <Line rows={live.rows} allocated={live.allocated} label={reference ? 'A' : null} />
      {reference && <Line rows={reference.rows} allocated={reference.allocated} label="B" />}
    </div>
  );
}

export default BlendAllocationBar;
